import { VariableLink as Base } from "@corca/tiptap";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { reaction } from "mobx";

import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import { computeVariableLinkDecorations, markSymbol } from "./decorations";

import type { VariableLinkDecorations } from "./decorations";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { EditorState } from "@tiptap/pm/state";

type VariableLinkOptions = {
  doc: DocumentModel;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    variableLink: {
      setVariableLink: (variable: string) => ReturnType;
      unsetVariableLink: () => ReturnType;
      refreshVariableLinks: () => ReturnType;
    };
  }
}

const REFRESH_META = "refreshVariableLinks";

const variableLinkKey = new PluginKey<VariableLinkDecorations>("variableLinkDecorations");

type HighlightSource = "hover" | "cursor" | "math";

function resolveVariableNode(doc: DocumentModel, hoverKey: string): NodeModel<"variable"> | null {
  const node = doc.nodes.get(hoverKey);
  if (node?.is(VariableNodeModel)) {
    return node;
  }
  return null;
}

// A range spanning two different links is ambiguous, so it highlights nothing.
function selectionSymbol(state: EditorState): string | null {
  const { empty, $from, from, to } = state.selection;
  if (empty) {
    return markSymbol($from.marks());
  }

  let symbol: string | null = null;
  let conflict = false;
  state.doc.nodesBetween(from, to, (node) => {
    if (conflict) {
      return false;
    }
    if (!node.isText) {
      return true;
    }
    const found = markSymbol(node.marks);
    if (found !== null && symbol !== null && symbol !== found) {
      conflict = true;
      return false;
    }
    symbol ??= found;
    return true;
  });

  return conflict ? null : symbol;
}

// Only linked variables count, so typing in unrelated math leaves the signature untouched.
function variableColorSignature(doc: DocumentModel, linked: Set<string> | undefined): string {
  return doc
    .getNodesByType(VariableNodeModel.type)
    .filter((node) => linked?.has(node.labeledValue))
    .map((node) => `${node.labeledValue}:${node.color}`)
    .sort()
    .join("|");
}

export const VariableLink = Base.extend<VariableLinkOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      doc: null as unknown as DocumentModel,
    };
  },

  addCommands() {
    return {
      setVariableLink:
        (variable: string) =>
        ({ chain }) => {
          return chain().setMark(this.name, { variable }).run();
        },
      unsetVariableLink:
        () =>
        ({ chain }) => {
          return chain().unsetMark(this.name, { extendEmptyMarkRange: true }).run();
        },
      refreshVariableLinks:
        () =>
        ({ commands }) => {
          return commands.setMeta(REFRESH_META, true);
        },
    };
  },

  addProseMirrorPlugins() {
    const { doc } = this.options;

    // Prose hover beats the caret, which is often parked in a link while the pointer is elsewhere.
    const active: Record<HighlightSource, string | null> = {
      hover: null,
      cursor: null,
      math: null,
    };
    const setSymbol = (source: HighlightSource, symbol: string | null) => {
      if (active[source] === symbol) {
        return;
      }
      active[source] = symbol;
      const next = active.hover ?? active.cursor ?? active.math;
      doc.editor.setHighlightedVariableSymbols(next ? new Set([next]) : new Set());
    };

    return [
      new Plugin<VariableLinkDecorations>({
        key: variableLinkKey,
        state: {
          init: (_, state) => computeVariableLinkDecorations(state.doc, doc),
          apply(tr, prev) {
            if (tr.docChanged || tr.getMeta(REFRESH_META)) {
              return computeVariableLinkDecorations(tr.doc, doc);
            }
            return prev;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)?.decorations ?? null;
          },
          handleDOMEvents: {
            mouseover: (_view, event) => {
              const target = event.target as HTMLElement | null;
              const span = target?.closest?.("[data-variable-link]");
              setSymbol("hover", span?.getAttribute("data-variable-link") ?? null);
              return false;
            },
            mouseleave: () => {
              setSymbol("hover", null);
              return false;
            },
            blur: () => {
              setSymbol("cursor", null);
              return false;
            },
            focus: (view) => {
              setSymbol("cursor", selectionSymbol(view.state));
              return false;
            },
          },
        },
        view(view) {
          const disposeHoverReaction = reaction(
            () => {
              const hoverKey = doc.nodeHover.current;
              return hoverKey ? (resolveVariableNode(doc, hoverKey)?.labeledValue ?? null) : null;
            },
            (symbol) => {
              const linked = variableLinkKey.getState(view.state)?.linkedSymbols;
              setSymbol("math", symbol !== null && linked?.has(symbol) ? symbol : null);
            },
          );

          // Variable colors live in yNodesMap, not the PM doc, so repaint from here.
          // Deferred: the reaction fires inside doc.transact while a paste is dispatching.
          let destroyed = false;
          const disposeColorReaction = reaction(
            () => variableColorSignature(doc, variableLinkKey.getState(view.state)?.linkedSymbols),
            () => {
              queueMicrotask(() => {
                if (!destroyed) {
                  doc.editor.tiptap?.commands.refreshVariableLinks?.();
                }
              });
            },
          );

          return {
            update(updatedView, prevState) {
              // Selection moves produce no DOM event and no docChanged, so read the caret here.
              if (
                prevState.selection.eq(updatedView.state.selection) &&
                prevState.doc === updatedView.state.doc
              ) {
                return;
              }
              setSymbol(
                "cursor",
                updatedView.hasFocus() ? selectionSymbol(updatedView.state) : null,
              );
            },
            destroy() {
              destroyed = true;
              disposeHoverReaction();
              disposeColorReaction();
            },
          };
        },
      }),
    ];
  },
});
